import React, { useState, useEffect, useContext } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import {
  makeStyles,
  Paper,
  Tabs,
  Tab,
  Typography,
  Box,
  CircularProgress,
  Button,
  Avatar
} from "@material-ui/core";
import { ArrowBack } from "@material-ui/icons";
import { useTitle } from "../../hooks/useTitle";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";

import HistoryTimeline from "./components/HistoryTimeline";
import AttachmentsGallery from "./components/AttachmentsGallery";
import UploadFileModal from "./components/UploadFileModal";
import MedicalObservations from "./components/MedicalObservations";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  headerCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    backgroundColor: theme.palette.background.default
  },
  avatar: {
    width: theme.spacing(8),
    height: theme.spacing(8),
    fontSize: "2rem",
    backgroundColor: theme.palette.primary.main
  },
  tabPanel: {
    paddingTop: theme.spacing(2),
  }
}));

const PatientHistory = () => {
  const classes = useStyles();
  const history = useHistory();
  const { contactId } = useParams();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const isDoctorOnly = user?.isDoctor && user?.profile !== "admin";
  const [loading, setLoading] = useState(true);
  const [patientData, setPatientData] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  const queryParams = new URLSearchParams(location.search);
  const patientName = queryParams.get("patientName");

  const titleName = patientData 
    ? (patientName || patientData.contact.name) 
    : (patientName || "Cargando...");

  useTitle("Expediente de " + titleName); 
  
  const fetchData = async () => {
    try {
      const params = {
        patientName: patientName,
        pageNumber: 1
      };

      if (isDoctorOnly) {
        params.doctorId = user.id;
      }
      
      const { data } = await api.get("/medical-history/patients/" + contactId, { params });
      setPatientData(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [contactId, patientName]);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </div>
    );
  }

  if (!patientData) {
    return <div>Paciente no encontrado</div>;
  }

  const { contact, history: appointments, attachments, observations } = patientData;
  const displayName = patientName ? patientName : (contact.name || "Desconocido");

  return (
    <MainContainer>
      <MainHeader>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Button onClick={() => history.goBack()} startIcon={<ArrowBack />}>
                Volver
            </Button>
            <Title>Expediente Médico</Title>
        </div>
        <MainHeaderButtonsWrapper>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Paper variant="outlined" className={classes.headerCard}>
            <Avatar src={contact.profilePicUrl} className={classes.avatar}>
                {displayName.charAt(0).toUpperCase()}
            </Avatar>
            <div style={{ flex: 1 }}>
                <Typography variant="h5">{displayName}</Typography>
                {patientName ? (
                    <Typography variant="body2" color="textSecondary">
                        Titular de cuenta: {contact.name}
                    </Typography>
                ) : (
                    <Typography variant="body2" color="textSecondary">
                        Email: {contact.email || "—"}
                    </Typography>
                )}
            </div>
        </Paper>

        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Historia Clínica" />
          <Tab label="Archivos y Estudios" />
          <Tab label="Observaciones" />
        </Tabs>

        {tabIndex === 0 && (
            <div className={classes.tabPanel}>
                {appointments.length > 0 ? (
                    <HistoryTimeline 
                      appointments={appointments} 
                      isDoctorOnly={isDoctorOnly}
                    />
                ) : (
                    <Typography align="center" color="textSecondary" style={{ marginTop: 20 }}>
                        No hay historial de citas registrado.
                    </Typography>
                )}
            </div>
        )}

        {tabIndex === 1 && (
            <div className={classes.tabPanel}>
                <AttachmentsGallery 
                    files={attachments} 
                    onUploadClick={() => setUploadModalOpen(true)}
                    onFileDeleted={() => fetchData()}
                    onFileUpdated={() => fetchData()}
                />
            </div>
        )}

        <UploadFileModal
          open={uploadModalOpen}
          onClose={() => setUploadModalOpen(false)}
          contactId={contact.id}
          patientName={patientName}
          appointments={appointments}
          onFileUploaded={fetchData}
          isDoctor={user?.profile == "doctor"}
        />

        {tabIndex === 2 && (
          <div className={classes.tabPanel}>
            <MedicalObservations
              observations={observations}
              contactId={contact.id}
              patientName={patientName}
              onCreated={fetchData}
            />
          </div>
        )}

      </Paper>
    </MainContainer>
  );
};

export default PatientHistory;